import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userSettingsApi } from '../services/userSettingsApi';
import logger from './logger';
import { API_BASE_URL } from './apiConfig';
import { api } from './apiFetch';

// Create the Auth Context
const AuthContext = createContext(null);

// Permissions Context
const PermissionsContext = createContext([]);
export const usePermissions = () => useContext(PermissionsContext);

// Create a custom hook to use the Auth Context
export const useAuth = () => useContext(AuthContext);

// Global function to update user profile (for external components)
export const updateUserProfile = (updatedProfile) => {
  if (window.authContextRef && window.authContextRef.setUserProfile) {
    window.authContextRef.setUserProfile(updatedProfile);
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [currentPage, setCurrentPage] = useState('home');
  const [userPermissions, setUserPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Set up global reference for external profile updates and 401 handling
  useEffect(() => {
    window.authContextRef = { setUserProfile };
    window.updateUserProfile = updateUserProfile;
    window.handleGlobal401 = handleSessionTimeout;
    
    return () => {
      delete window.authContextRef;
      delete window.updateUserProfile;
      delete window.handleGlobal401;
    };
  }, []);

  // Helper functions for token management
  const getAccessToken = () => localStorage.getItem('access_token');
  const setAccessToken = (token) => {
    // Store access token in localStorage
    localStorage.setItem('access_token', token);
  };
  const clearAccessToken = () => localStorage.removeItem('access_token');
  const getRefreshToken = () => localStorage.getItem('refresh_token');
  const setRefreshToken = (token) => localStorage.setItem('refresh_token', token);
  const clearRefreshToken = () => localStorage.removeItem('refresh_token');
  
  // Helper to check if JWT token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const isExpired = payload.exp < currentTime;
      
      // Add logging for patient tokens
      const patientData = localStorage.getItem('patientData');
      if (patientData) {
        const timeUntilExpiry = payload.exp - currentTime;
       
      }
      
      return isExpired;
    } catch (error) {
      return true; // If we can't parse the token, consider it expired
    }
  };

  // Load session from localStorage on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUserProfile = localStorage.getItem('userProfile');
        const storedAccessToken = getAccessToken();
        const storedRefreshToken = getRefreshToken();
        const storedPatientData = localStorage.getItem('patientData');
        const storedClinicData = localStorage.getItem('clinicData');
        
        if (storedAccessToken) {
          // Check if token is expired first
          if (isTokenExpired(storedAccessToken)) {
            // Stored token is expired, attempting refresh
            // Token is expired, try to refresh it
            if (storedRefreshToken) {
              // Try to refresh token first
              try {
                const refreshRes = await fetch(`${API_BASE_URL}/api/refresh-token`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ refresh_token: storedRefreshToken })
                });
                
                if (refreshRes.ok) {
                  const { access_token } = await refreshRes.json();
                  setAccessToken(access_token);
                  // Continue with the new token
                } else {
                  // Refresh failed, clear everything
                  localStorage.clear();
                  setIsAuthenticated(false);
                  setUserProfile(null);
                  setIsLoading(false);
                  return;
                }
      } catch (error) {
        logger.error('Token refresh failed:', error);
                localStorage.clear();
                setIsAuthenticated(false);
                setUserProfile(null);
                setIsLoading(false);
                return;
              }
            } else {
              // No refresh token, clear everything
              localStorage.clear();
              setIsAuthenticated(false);
              setUserProfile(null);
              setIsLoading(false);
              return;
            }
          }
          
          // Check if this is a patient login
          if (storedPatientData) {
            try {
              // For patients, try to validate with a patient-specific endpoint
              const patientProfile = JSON.parse(storedPatientData);
              
              // First try the profile endpoint
              let res = await fetch(`${API_BASE_URL}/api/patients/profile`, {
                headers: { 'Authorization': `Bearer ${storedAccessToken}` },
                credentials: 'include',
              });
              
              if (res.ok) {
                // Patient token is valid, restore session
                setIsAuthenticated(true);
                setUserProfile(patientProfile);
                setCurrentPage('patient-dashboard');
                return;
              } else if (res.status === 401 && storedRefreshToken) {
                // Patient token expired, attempting refresh
                // Try to refresh token for patient
                const refreshRes = await fetch(`${API_BASE_URL}/api/patients/refresh-token`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ refresh_token: storedRefreshToken })
                });
                
                if (refreshRes.ok) {
                  const { access_token } = await refreshRes.json();
                  // Patient token refresh successful
                  setAccessToken(access_token);
                  // Retry profile fetch with new token
                  const retryRes = await fetch(`${API_BASE_URL}/api/patients/profile`, {
                    headers: { 'Authorization': `Bearer ${access_token}` },
                    credentials: 'include',
                  });
                  
                  if (retryRes.ok) {
                    // Patient profile fetch successful after refresh
                    setIsAuthenticated(true);
                    setUserProfile(patientProfile);
                    setCurrentPage('patient-dashboard');
                    return;
                  } else {
                    // Patient profile fetch failed after refresh
                  }
                } else {
                  // Patient token refresh failed
                }
              } else if (res.status === 404) {
                // Profile endpoint not found, try a simple validation with appointments endpoint
                const validationRes = await fetch(`${API_BASE_URL}/api/patients/${patientProfile.patient_id}/my-appointments?limit=1`, {
                  headers: { 'Authorization': `Bearer ${storedAccessToken}` },
                  credentials: 'include',
                });
                
                if (validationRes.ok || validationRes.status === 404) {
                  // Token is valid (even if no appointments found), restore session
                  setIsAuthenticated(true);
                  setUserProfile(patientProfile);
                  setCurrentPage('patient-dashboard');
                  return;
                }
              }
            } catch (error) {
              logger.error('Patient token validation failed:', error);
            }
          }
          
          // Check if this is a clinic/staff login
          if (storedClinicData || !storedPatientData) {
            try {
              // Try to validate token with /api/me for staff/admin
              const res = await fetch(`${API_BASE_URL}/api/me`, {
                headers: { 'Authorization': `Bearer ${storedAccessToken}` },
                credentials: 'include',
              });
              
              if (res.ok) {
                const profileData = await res.json();
                setIsAuthenticated(true);
                setUserProfile(profileData);
                localStorage.setItem('userProfile', JSON.stringify(profileData));
                setCurrentPage('dashboard');
                return;
              } else if (res.status === 401 && storedRefreshToken) {
                // Try to refresh token
                const refreshRes = await fetch(`${API_BASE_URL}/api/refresh-token`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ refresh_token: storedRefreshToken })
                });
                
                if (refreshRes.ok) {
                  const { access_token } = await refreshRes.json();
                  setAccessToken(access_token);
                  localStorage.setItem('access_token', access_token);
                  
                  // Retry /api/me with new token
                  const retryRes = await fetch(`${API_BASE_URL}/api/me`, {
                    headers: { 'Authorization': `Bearer ${access_token}` },
                    credentials: 'include',
                  });
                  
                  if (retryRes.ok) {
                    const profileData = await retryRes.json();
                    setIsAuthenticated(true);
                    setUserProfile(profileData);
                    localStorage.setItem('userProfile', JSON.stringify(profileData));
                    setCurrentPage('dashboard');
                    return;
                  }
                }
              }
            } catch (error) {
              logger.error('Staff token validation failed:', error);
              // Don't clear localStorage on network errors - allow user to stay logged in
              // Only clear if we're certain the token is invalid (not just a network issue)
              // For now, try to use stored profile as fallback
              if (storedUserProfile) {
                try {
                  const parsedProfile = JSON.parse(storedUserProfile);
                  setIsAuthenticated(true);
                  setUserProfile(parsedProfile);
                  setCurrentPage('dashboard');
                  return;
                } catch (parseError) {
                  logger.error('Failed to parse stored user profile:', parseError);
                }
              }
            }
          }
          
          // If we get here, token validation failed for both patient and staff
          // Only clear if we have no stored profile to fall back to
          if (!storedUserProfile && !storedPatientData) {
            localStorage.clear();
            setIsAuthenticated(false);
            setUserProfile(null);
          } else if (storedUserProfile) {
            // Fallback: use stored profile if token validation failed (might be network issue)
            try {
              const parsedProfile = JSON.parse(storedUserProfile);
              setIsAuthenticated(true);
              setUserProfile(parsedProfile);
              setCurrentPage('dashboard');
              // Don't set isLoading to false here - let it finish below
            } catch (parseError) {
              logger.error('Failed to parse stored user profile:', parseError);
              localStorage.clear();
              setIsAuthenticated(false);
              setUserProfile(null);
            }
          }
        } else if (storedUserProfile && storedAccessToken) {
          // Fallback: if both exist, trust localStorage (legacy behavior)
          // This handles cases where token validation hasn't completed yet
          // IMPORTANT: This prevents redirect to home when duplicating tabs
          const parsedProfile = JSON.parse(storedUserProfile);
          setIsAuthenticated(true);
          setUserProfile(parsedProfile);
          setCurrentPage('dashboard');
          // Don't set isLoading to false here - let it finish below
        } else if (storedAccessToken && !isTokenExpired(storedAccessToken) && storedUserProfile) {
          // If we have a valid (non-expired) token and stored profile,
          // restore session immediately to prevent redirect on tab duplication
          // This is a critical fix for tab duplication issue
          try {
            const parsedProfile = JSON.parse(storedUserProfile);
            setIsAuthenticated(true);
            setUserProfile(parsedProfile);
            setCurrentPage('dashboard');
          } catch (parseError) {
            logger.error('Failed to parse stored user profile:', parseError);
          }
        }
      } catch (error) {
        // Clear corrupted data
        logger.error('Auth initialization error:', error);
        localStorage.clear();
        setIsAuthenticated(false);
        setUserProfile(null);
      } finally {
        setIsLoading(false);
      }
    };
    initializeAuth();
  }, []);

  // Extract permissions from userProfile
  useEffect(() => {
    if (userProfile && userProfile.role && Array.isArray(userProfile.role.permissions)) {
      const permissions = userProfile.role.permissions.map(p => p.name);
      setUserPermissions(permissions);
    } else if (userProfile && userProfile.permissions && Array.isArray(userProfile.permissions)) {
      // Alternative structure: permissions directly on userProfile
      const permissions = userProfile.permissions.map(p => typeof p === 'string' ? p : p.name);
      setUserPermissions(permissions);
    } else {
      setUserPermissions([]);
    }
  }, [userProfile]);

  // Periodic token validation check
  useEffect(() => {
    if (!isAuthenticated || !userProfile) return;

    const checkTokenValidity = async () => {
      try {
        const token = getAccessToken();
        if (!token || isTokenExpired(token)) {
          // Token expired during periodic check, logging out
          handleSessionTimeout();
          return;
        }

        // Validate token with backend every 5 minutes
        // Check if this is a patient login
        const patientData = localStorage.getItem('patientData');
        let validationEndpoint = `${API_BASE_URL}/api/me`;
        
        if (patientData) {
          // For patients, use a patient-specific endpoint
          const patientProfile = JSON.parse(patientData);
          validationEndpoint = `${API_BASE_URL}/api/patients/profile`;
          // Periodic patient token validation
        }
        
        const res = await fetch(validationEndpoint, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });

        if (res.status === 401) {
          // Patient token invalid during periodic check, logging out
          handleSessionTimeout();
          return;
        } else {
          // Patient token validation successful
        }
      } catch (error) {
        logger.error('Token validation check failed:', error);
        // Don't logout on network errors, just log the issue
      }
    };

    // Check every 5 minutes (300000 ms)
    const interval = setInterval(checkTokenValidity, 300000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, userProfile]);

  // Check token validity when page becomes visible (user returns to tab)
  useEffect(() => {
    if (!isAuthenticated || !userProfile) return;

    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // Page became visible, check token immediately
        const token = getAccessToken();
        if (!token || isTokenExpired(token)) {
          // Token expired when page became visible, logging out
          handleSessionTimeout();
          return;
        }

        // Quick validation with backend
        try {
          // Check if this is a patient login
          const patientData = localStorage.getItem('patientData');
          let validationEndpoint = `${API_BASE_URL}/api/me`;
          
          if (patientData) {
            // For patients, use a patient-specific endpoint
            const patientProfile = JSON.parse(patientData);
            validationEndpoint = `${API_BASE_URL}/api/patients/${patientProfile.patient_id}/my-appointments?limit=1`;
          }
          
          const res = await fetch(validationEndpoint, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
          });

          if (res.status === 401) {
            // Token invalid when page became visible, logging out
            handleSessionTimeout();
            return;
          }
        } catch (error) {
          logger.error('Token validation on visibility change failed:', error);
          // Don't logout on network errors
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, userProfile]);

  // User activity timeout - logout after 30 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated || !userProfile) return;

    let inactivityTimer;
    let warningTimer;
    let userSessionTimeout = 30; // Default 30 minutes, will be updated from user settings
    let userWarningTime = 5; // Default 5 minutes warning, will be updated from user settings

    // Fetch user session timeout settings
    const fetchUserSessionSettings = async () => {
      try {
        const timeoutMinutes = await userSettingsApi.getSessionTimeout();
        userSessionTimeout = timeoutMinutes;
        userWarningTime = Math.min(5, Math.floor(timeoutMinutes * 0.1)); // 10% of timeout or max 5 minutes
        // User session timeout configured
      } catch (error) {
        logger.error('Error fetching user session settings:', error);
        // Keep default values
      }
    };

    const resetInactivityTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }

      // Set warning timer
      warningTimer = setTimeout(async () => {
        // Check if session is still valid before showing warning
        const token = getAccessToken();
        if (!token || isTokenExpired(token)) {
          // Session already expired, logging out immediately instead of showing warning
          handleSessionTimeout();
          return;
        }

        // Quick validation with backend
        try {
          // Check if this is a patient login
          const patientData = localStorage.getItem('patientData');
          let validationEndpoint = `${API_BASE_URL}/api/me`;
          
          if (patientData) {
            // For patients, use a patient-specific endpoint
            const patientProfile = JSON.parse(patientData);
            validationEndpoint = `${API_BASE_URL}/api/patients/profile`;
          }
          
          const res = await fetch(validationEndpoint, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
          });

          if (res.status === 401) {
            // Session already expired, logging out immediately instead of showing warning
            handleSessionTimeout();
            return;
          }
          
          // If validation succeeds, continue with warning
        } catch (error) {
          logger.error('Token validation before warning failed:', error);
          // Don't show warning if we can't validate, just log the issue
          // This prevents immediate session timeout on network errors
          return;
        }

        // Session is still valid, show warning notification
        // Double-check that user is still authenticated before showing warning
        if (isAuthenticated && window.showSessionWarning) {
          window.showSessionWarning();
        } else if (isAuthenticated) {
          // Fallback: show browser alert
          alert('Your session will expire in 5 minutes due to inactivity. Please interact with the page to stay logged in.');
        }
      }, (userSessionTimeout - userWarningTime) * 60 * 1000);

      // Set logout timer
      inactivityTimer = setTimeout(() => {
        // User inactive for configured time, logging out
        handleSessionTimeout();
      }, userSessionTimeout * 60 * 1000);
    };

    // Only start session timeout for staff users, not patients
    const patientData = localStorage.getItem('patientData');
    
    // Define activity events and handler outside conditional blocks
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => {
      resetInactivityTimer();
    };
    
    if (!patientData) {
      // Fetch user session settings when component mounts (staff only)
      fetchUserSessionSettings();

      // Set up activity listeners
      activityEvents.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });

      // Initialize timer with a small delay to prevent immediate timeout after login
      setTimeout(() => {
        resetInactivityTimer();
      }, 2000); // 2 second delay
    } else {
      // Patient login detected, skipping session timeout timer
    }

    return () => {
      // Only cleanup timers for staff users
      if (!patientData) {
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
        }
        if (warningTimer) {
          clearTimeout(warningTimer);
        }
        activityEvents.forEach(event => {
          document.removeEventListener(event, handleActivity, true);
        });
      }
    };
  }, [isAuthenticated, userProfile]);

  // API_BASE_URL is imported from apiConfig.js

  // Function to handle login/session creation
  const handleLogin = async (accessToken, refreshToken, clinicData = null, skipNavigation = false) => {
    try {
      // Handle login with provided tokens
      
      setIsAuthenticated(true);
      setAccessToken(accessToken);
      if (refreshToken) setRefreshToken(refreshToken);

      // Store clinic data if provided (from clinic login)
      if (clinicData) {
        localStorage.setItem('clinicData', JSON.stringify(clinicData));
      }

      // Fetch latest user profile from /api/me
      const res = await fetch(`${API_BASE_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });
      
      if (res.ok) {
        const profileData = await res.json();
        setUserProfile(profileData);
        localStorage.setItem('userProfile', JSON.stringify(profileData));

        // Send login notification
        try {
          const deviceInfo = userSettingsApi.getDeviceInfo();
          const locationInfo = await userSettingsApi.getLocationInfo();
          
          await userSettingsApi.sendLoginNotification({
            session_id: 'session-' + Date.now(), // Generate a session ID
            ip_address: null, // Will be detected by backend
            user_agent: navigator.userAgent,
            device_info: deviceInfo,
            location_info: locationInfo
          });
        } catch (notificationError) {
          logger.error('Failed to send login notification:', notificationError);
          // Don't fail login if notification fails
        }
      } else {
        setUserProfile(null);
        localStorage.removeItem('userProfile');
        throw new Error('Failed to fetch user profile');
      }
    } catch (err) {
      // Handle login failure
      setIsAuthenticated(false);
      setUserProfile(null);
      clearAccessToken();
      clearRefreshToken();
      localStorage.clear();
      throw err;
    }
    
    // Only navigate if not skipped (allows caller to handle navigation)
    if (!skipNavigation) {
      setCurrentPage('dashboard');
      navigate('/dashboard');
    }
  };

  // Function to handle patient login
  const handlePatientLogin = async (accessToken, refreshToken, patientData = null) => {
    try {
      setIsAuthenticated(true);
      setAccessToken(accessToken);
      if (refreshToken) setRefreshToken(refreshToken);

      // Store patient data if provided
      if (patientData) {
        localStorage.setItem('patientData', JSON.stringify(patientData));
      }

      // Set patient profile
      if (patientData) {
        const patientProfile = {
          ...patientData,
          role: { name: 'Patient', permissions: ['patient_read', 'patient_update'] }
        };
        setUserProfile(patientProfile);
        localStorage.setItem('userProfile', JSON.stringify(patientProfile));
      }
    } catch (err) {
      // Handle login failure
      setIsAuthenticated(false);
      setUserProfile(null);
      clearAccessToken();
      clearRefreshToken();
      localStorage.clear();
      throw err;
    }

    setCurrentPage('patient-dashboard');
    navigate('/patient-dashboard');
  };

  // Function to handle logout
  const handleLogout = async () => {
    // Check if MFA setup is required - block logout if so
    const requiresMfaSetup = localStorage.getItem('requires_mfa_setup') === 'true';
    if (requiresMfaSetup) {
      // Show error message and redirect to MFA setup
      alert('You must set up two-factor authentication before logging out. Please enable 2FA in your security settings.');
      window.location.href = '/dashboard/user-settings?mfa_setup_required=true';
      return;
    }
    
    try {
      // Use apiFetch utility for automatic CSRF token handling
      const logoutRes = await api.post('/api/logout', {});
      
      // Check if logout was blocked due to MFA requirement
      if (!logoutRes.ok) {
        const errorData = await logoutRes.json();
        if (errorData.detail?.error?.code === 'MFA_SETUP_REQUIRED') {
          alert('You must set up two-factor authentication before logging out. Please enable 2FA in your security settings.');
          window.location.href = '/dashboard/user-settings?mfa_setup_required=true';
          return;
        }
      }
    } catch (err) {
      // Log error but don't throw - user should still be logged out locally
      logger.error('Logout error:', err);
    } finally {
      // Always clear local state regardless of server response
      setIsAuthenticated(false);
      setUserProfile(null);
      setUserPermissions([]);
      clearAccessToken();
      clearRefreshToken();
      
      // Preserve user preferences before clearing localStorage
      const preservedProfilePicture = localStorage.getItem('preservedProfilePicture');
      const preservedTabConfig = localStorage.getItem(`appointments-tab-order-${userProfile?.id || userProfile?.user_id}`);
      
      // Also preserve general tab config as backup
      const generalTabConfig = localStorage.getItem('appointments-tab-order');
      
    if (preservedProfilePicture) {
      // Preserving profile picture during logout
    }
      
      localStorage.clear();
      
      // Restore preserved data after clearing
    if (preservedProfilePicture) {
      localStorage.setItem('preservedProfilePicture', preservedProfilePicture);
      // Profile picture restored after logout
    }
      
      // Restore tab configuration if it exists
      if (preservedTabConfig) {
        localStorage.setItem(`appointments-tab-order-${userProfile?.id || userProfile?.user_id}`, preservedTabConfig);
        // Tab configuration restored after logout
      } else if (generalTabConfig) {
        // Fallback to general config if user-specific doesn't exist
        localStorage.setItem('appointments-tab-order', generalTabConfig);
      }
      
      setCurrentPage('home');
      navigate('/');
    }
  };

  // Function to handle session timeout/401 errors
  const handleSessionTimeout = (errorCode = 'TOKEN_EXPIRED') => {
    // Check if user is a patient BEFORE clearing localStorage
    const isPatient = isPatientLogin();
    
    // Session timeout detected, logging out user
    // Clear all auth state immediately
    setIsAuthenticated(false);
    const currentUserProfile = userProfile; // Save userProfile before clearing
    setUserProfile(null);
    setUserPermissions([]);
    clearAccessToken();
    clearRefreshToken();
    
    // Preserve user preferences before clearing localStorage
    const preservedProfilePicture = localStorage.getItem('preservedProfilePicture');
    const preservedTabConfig = currentUserProfile 
      ? localStorage.getItem(`appointments-tab-order-${currentUserProfile?.id || currentUserProfile?.user_id}`)
      : null;
    const generalTabConfig = localStorage.getItem('appointments-tab-order');
    
    if (preservedProfilePicture) {
      // Preserving profile picture during session timeout
    }
    
    localStorage.clear();
    
    // Restore preserved data after clearing
    if (preservedProfilePicture) {
      localStorage.setItem('preservedProfilePicture', preservedProfilePicture);
      // Profile picture restored after session timeout
    }
    
    // Restore tab configuration if it exists
    if (preservedTabConfig && currentUserProfile) {
      localStorage.setItem(`appointments-tab-order-${currentUserProfile?.id || currentUserProfile?.user_id}`, preservedTabConfig);
      // Tab configuration restored after session timeout
    } else if (generalTabConfig) {
      // Fallback to general config if user-specific doesn't exist
      localStorage.setItem('appointments-tab-order', generalTabConfig);
    }
    
    setCurrentPage('home');
    
    // Redirect to appropriate login page based on user type
    if (isPatient) {
      navigate('/patient-login');
    } else {
      navigate('/login');
    }
  };

  // Helper to check if user logged in with clinic context
  const isClinicLogin = () => {
    const clinicData = localStorage.getItem('clinicData');
    return clinicData !== null;
  };

  // Helper to check if user is a patient
  const isPatientLogin = () => {
    const patientData = localStorage.getItem('patientData');
    return patientData !== null;
  };

  const authContextValue = {
    isAuthenticated,
    userProfile,
    handleLogin,
    handlePatientLogin,
    handleLogout,
    handleSessionTimeout,
    currentPage,
    setCurrentPage,
    getAccessToken,
    userPermissions,
    isClinicLogin,
    isPatientLogin,
    isLoading,
  };

  // Add a helper component for test environments to expose test IDs
  function AuthTestIds({ isAuthenticated, userProfile }) {
    if (process.env.NODE_ENV !== 'test') return null;
    return (
      <>
        <div data-testid="isAuthenticated">{isAuthenticated.toString()}</div>
        <div data-testid="user">{userProfile ? (userProfile.clinicId || userProfile.username || 'null') : 'null'}</div>
      </>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {/* Expose test IDs for testing */}
      <AuthTestIds isAuthenticated={isAuthenticated} userProfile={userProfile} />
      <PermissionsContext.Provider value={userPermissions}>
        {children}
      </PermissionsContext.Provider>
    </AuthContext.Provider>
  );
};